
import React, { useEffect, useState } from 'react'
import { Col, FormGroup, FormLabel, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { format, isAfter, isBefore, parseISO } from 'date-fns'

import { useDatePicker } from '@/Hooks/UseDatePicker'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { useProject } from '@/Hooks/UseProject'
import useTranslation from '@/Hooks/UseTranslation'
import { useTeams } from '@/Hooks/UseTeam'
import { updateConstraintsAction } from '@/Redux/Actions/ProgrammationManagerActions'
import { removeConstraint, updateConstraint } from '@/Services/ProgrammationManagerService'

import Modal from '@/Components/Modal/Modal'
import Textarea from '../../components/Textarea'

import InputSelect from '../../components/InputSelect'
import InputDropdown from '../../components/InputDropdown/InputDropdown'
import InputDropdownUser from '../../components/InputDropdown/InputDropdownUser'
import RestrictionCommentInput from '../../components/RestrictionCommentInput'
import RestrictionComment from '../../components/RestrictionComment'
import InputText from '../../components/InputText'

import Spinner from '@/Components/SvgImages/Spinner/Spinner'
import TrashIcon from '@/Components/SvgImages/TrashIcon'

import './ConstraintEditor.scss'

interface Props {
  constraint: any
  subTask?: any
  onClose: () => void
}

const ConstraintEditor = ({ constraint, subTask, onClose }: Props) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { showDatePicker } = useDatePicker()

  const { project, nonWorkingDays } = useProject()

  const { itemOpened, setEditingItem, patchItemOpened } = useProgrammation()

  const { users, allInternalProfiles } = useTeams()

  const [ name, setName ] = useState(constraint?.content)
  const [ description, setDescription ] = useState(constraint?.content)
  const [ family, setFamily ] = useState(constraint?.family)
  const [ responsable, setResponsable ] = useState(constraint?.responsable?._id || '')
  const [ checked, setChecked ] = useState(constraint?.checked)
  const [ deleting, setDeleting ] = useState(false)
  const [ comments, setComments ] = useState(constraint?.comments || [])
  const [ promiseDate, setPromiseDate ] = useState(constraint?.promiseDate || '')
  const [ requiredDate, setRequiredDate ] = useState(constraint?.requiredDate || '')
  const [ releaseDate, setReleaseDate ] = useState(constraint?.releaseDate || '')

  const task = subTask || itemOpened

  const familiesOptions = [
    { _id: 'general', name: t('CONSTRAINT_CREATOR:FamilyGeneral') },
    { _id: 'coordination', name: t('CONSTRAINT_CREATOR:FamilyCoordination') },
    { _id: 'resource', name: t('CONSTRAINT_CREATOR:FamilyResource') },
    { _id: 'time', name: t('CONSTRAINT_CREATOR:FamilyTime') },
    { _id: 'space', name: t('CONSTRAINT_CREATOR:FamilySpace') },
    { _id: 'design', name: t('CONSTRAINT_CREATOR:FamilyDesign') },
    { _id: 'logistic', name: t('CONSTRAINT_CREATOR:FamilyLogistic') },
  ]

  const handleUpdate = async (constraint: any) => {

    setEditingItem(true)

    const [ error, data ] = await updateConstraint(task._id, constraint)

    setEditingItem(false)

    if (error) {

      // TODO: show error
      return
    }

    if (!subTask) {

      patchItemOpened({
        restrictions: data.task.restrictions
      })
    } else {
      const subTasks = itemOpened.subTasks

      subTasks.forEach((subTask: any) => {

        if (subTask._id === task._id) {

          subTask.restrictions = subTask.restrictions.map((c: any) => c._id === constraint._id ? constraint : c)
        }
      })

      patchItemOpened({
        subTasks
      })
    }

    dispatch(updateConstraintsAction({ task: data.task, parentTask: data.parentTask }))
  }

  const handleNameChange = (name: string) => {

    setName(name)

    handleUpdate({
      ...constraint,
      description: name,
    })
  }

  const handleDescriptionChange = (description: string) => {

    setDescription(description)

    handleUpdate({
      ...constraint,
      content: description,
    })
  }

  const handleFamilyChange = (family: string) => {

    setFamily(family)

    handleUpdate({
      ...constraint,
      family,
    })
  }

  const handleResponsableChange = (responsable: string) => {

    const foundInUsers = users.find((u: any) => u._id === responsable)
    const foundInInternalProfiles = allInternalProfiles.find((u: any) => u._id === responsable)

    let responsableModel

    if (foundInUsers) {
      responsableModel = 'User'
    } else if (foundInInternalProfiles) {
      responsableModel = 'InternalProfile'
    }

    setResponsable(responsable)

    handleUpdate({
      ...constraint,
      responsable,
      responsableModel,
    })
  }

  const openClampedDatePicker = (currentDate: string, onChange: (date: string) => void) => {

    if (!project.startsAt || !project.endsAt) return

    const current = currentDate ? parseISO(currentDate) : new Date()
    const projectStart = parseISO(project.startsAt)
    const projectEnd = parseISO(project.endsAt)

    let date: string

    if (isBefore(current, projectStart)) {
      date = format(projectStart, 'yyyy-MM-dd')
    } else if (isAfter(current, projectEnd)) {
      date = format(projectEnd, 'yyyy-MM-dd')
    } else {
      date = format(current, 'yyyy-MM-dd')
    }

    showDatePicker({
      date,
      projectStartDate: project.startsAt,
      projectEndDate: project.endsAt,
      nonWorkingDays,
      onChange,
    })
  }

  const handleShowPromiseDatePicker = () => openClampedDatePicker(promiseDate, (date: string) => {

    setPromiseDate(date)

    handleUpdate({
      ...constraint,
      promiseDate: date,
      requiredDate,
      releaseDate,
    })
  })

  const handleShowRequiredDatePicker = () => openClampedDatePicker(requiredDate, (date: string) => {

    setRequiredDate(date)

    handleUpdate({
      ...constraint,
      requiredDate: date,
      promiseDate,
      releaseDate,
    })
  })

  const handleShowReleaseDatePicker = () => openClampedDatePicker(releaseDate, (date: string) => {

    setReleaseDate(date)

    handleUpdate({
      ...constraint,
      releaseDate: date,
      promiseDate,
      requiredDate,
    })
  })

  const handleDelete = async () => {

    setEditingItem(true)
    setDeleting(true)

    const [ error, data ] = await removeConstraint(task._id, constraint._id)

    setEditingItem(false)
    setDeleting(false)

    onClose()

    if (error) {

      // TODO: show error
      return
    }

    if (!subTask) {

      patchItemOpened({
        restrictions: itemOpened.restrictions.filter((c: any) => c._id !== constraint._id)
      })
    } else {

      const subTasks = itemOpened.subTasks

      subTasks.forEach((subTask: any) => {

        if (subTask._id === task._id) {

          subTask.restrictions = subTask.restrictions.filter((c: any) => c._id !== constraint._id)
        }
      })

      patchItemOpened({
        subTasks
      })
    }

    dispatch(updateConstraintsAction({ task: data.task, parentTask: data.parentTask }))
  }

  useEffect(() => {

    setName(constraint?.description)
    setDescription(constraint?.content)
    setFamily(constraint?.family)
    setResponsable(constraint?.responsable?._id || '')
    setChecked(constraint?.checked)
    setPromiseDate(constraint?.promiseDate || '')
    setRequiredDate(constraint?.requiredDate || '')
    setReleaseDate(constraint?.releaseDate || '')
    setComments(constraint?.comments || [])
  }, [ constraint])

  return (
    <Modal
      show={!!constraint}
      title={constraint?.editable ? t('CONSTRAINT_EDITOR:EditTitle') : t('CONSTRAINT_EDITOR:ViewTitle')}
      onClose={onClose}
      optionsButtons={constraint?.editable && [{
        icon: (deleting
                ? <Spinner height={20} width={20} fill="#e21c1c" />
                : <TrashIcon height={20} width={20} color="#e21c1c" />
        ),
        onClick: () => handleDelete(),
        disabled: deleting
      }]}
      backdropClassName='constraint-editor-backdrop'
      className='constraint-editor'
      showCloseButton={true}
      scrollable
      zIndex='2'
    >
      {constraint?.relation && (
        <>
          {t('CONSTRAINT_ITEM:DependencyConstraintDescription')}
        </>
      )}
      {constraint && !constraint?.relation && (
        <>
        <FormGroup>
          <FormLabel>
            {t('CONSTRAINT_EDITOR:LabelName')}
          </FormLabel>
          <InputText
            placeholder="Name"
            value={name}
            onChange={(e: any) => handleNameChange(e)}
            disabled={constraint?.editable ? false : true}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>
            {t('CONSTRAINT_EDITOR:LabelDescription')}
          </FormLabel>
          <Textarea
            placeholder={t('CONSTRAINT_EDITOR:DescriptionPlaceholder')}
            value={description}
            onChange={(e: any) => handleDescriptionChange(e)}
            style={{ maxHeight: 60, resize: 'none' }}
            readOnly={constraint?.editable ? false : true}
          />
        </FormGroup>
          <FormGroup>
            <FormLabel>
              {t('CONSTRAINT_CREATOR:LabelFamily')}
            </FormLabel>
            <InputSelect
              // placeholder="Family"
              value={family}
              options={familiesOptions}
              onChange={(e: any) => handleFamilyChange(e)}
              disabled={!constraint?.editable}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>
              {t('CONSTRAINT_CREATOR:LabelAssignee')}
            </FormLabel>
            <InputDropdown
              value={responsable}
              placeholder="Responsible"
              options={[ ...users, ...allInternalProfiles ]}
              renderItem={(option: any) => (
                <InputDropdownUser
                  user={option}
                  isSelected={option._id === responsable}
                />
              )}
              onChange={(user_id: any) => handleResponsableChange(user_id)}
              disabled={!constraint?.editable}
            />
            {/* <InputSelect
              placeholder="Responsible"
              value={responsable}
              options={users.map((u: any) => ({ _id: u._id, name: `${u.name} ${u.surname}` }))}
              onChange={(e: any) => handleResponsableChange(e)}
            /> */}
          </FormGroup>
          <Row className='mb-4'>
            <FormGroup as={Col} sm={4}>
              <FormLabel>
                {t('CONSTRAINT_CREATOR:LabelPromiseDate')}
              </FormLabel>
              <InputText
                value={promiseDate ? format(parseISO(promiseDate), 'dd/MM/yyyy') : ''}
                placeholder="dd/mm/yyyy"
                onClick={() => handleShowPromiseDatePicker()}
                asButton
              />
            </FormGroup>
            <FormGroup as={Col} sm={4}>
              <FormLabel>
                {t('CONSTRAINT_CREATOR:LabelRequiredDate')}
              </FormLabel>
              <InputText
                value={requiredDate ? format(parseISO(requiredDate), 'dd/MM/yyyy') : ''}
                placeholder="dd/mm/yyyy"
                onClick={() => handleShowRequiredDatePicker()}
                asButton
              />
            </FormGroup>
            <FormGroup as={Col} sm={4}>
              <FormLabel>
                {t('CONSTRAINT_CREATOR:LabelReleaseDate')}
              </FormLabel>
              <InputText
                value={releaseDate ? format(parseISO(releaseDate), 'dd/MM/yyyy') : ''}
                placeholder="dd/mm/yyyy"
                onClick={() => handleShowReleaseDatePicker()}
                asButton
              />
            </FormGroup>
          </Row>

          <div>
            <RestrictionCommentInput taskId={task._id} restrictionId={constraint._id} onCommentsUpdated={setComments} />
            <div className="constraint-item-comments">
              {[...comments].reverse().map((comment: any, i: number) => (
                <div key={comment._id}>
                  <RestrictionComment
                    item={comment}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}

export default ConstraintEditor
